import { cx } from "../../components/ui/cx";
import type { StreamRun } from "../../lib/api/types";
import { formatDate, formatSpan } from "../../lib/format";
import { runTimeline } from "../../lib/uptime";

/**
 * The recent runs as one bar: dark while live, grey while starting, a red notch where a run ended by a fault.
 * Gaps are time the stream was stopped or waiting to retry.
 */
export function UptimeTimeline({ runs, now }: { runs: StreamRun[]; now: number }) {
  const t = runTimeline(runs, now);
  if (!t) return null;
  const pct = (x: number) => `${(x * 100).toFixed(3)}%`;
  const faults = t.segments.filter((s) => s.fault).length;
  return (
    <figure className="flex flex-col gap-3 border-b-2 border-ink px-5 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <span className="font-display text-3xl font-bold tabular-nums">{(t.uptime * 100).toFixed(1)}%<span className="ml-2 text-base font-medium text-muted">on air</span></span>
        <span className="font-mono text-xs text-muted">{formatSpan(t.liveMs)} live over {formatSpan(t.to - t.from)} · {faults} {faults === 1 ? "drop" : "drops"}</span>
      </div>
      <div className="relative h-9 overflow-hidden rounded-lg border-2 border-ink bg-paper" aria-hidden="true">
        {t.segments.map((s, i) => (
          <span key={s.id} className="absolute inset-y-0 flex origin-left motion-safe:animate-[grow_1s_var(--ease-out-soft)_both]" style={{ left: pct(s.start), width: pct(Math.max(s.end - s.start, 0.004)), animationDelay: `${i * 80}ms` }}>
            {s.live > s.start && <span className="h-full bg-faint/50" style={{ width: pct((s.live - s.start) / Math.max(s.end - s.start, 1e-9)) }} />}
            <span className={cx("h-full flex-1", !s.wentLive ? "bg-faint/50" : s.running ? "bg-live" : "bg-ink")} />
            {s.fault && <span className="h-full w-1 bg-accent" />}
          </span>
        ))}
      </div>
      <figcaption className="flex flex-col gap-2 font-mono text-xs text-muted">
        <span className="flex justify-between gap-4">
          <span>{formatDate(new Date(t.from).toISOString())}</span>
          <span>now</span>
        </span>
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <span className="inline-flex items-center gap-1.5"><i className="size-2.5 rounded-sm bg-live" />live now</span>
          <span className="inline-flex items-center gap-1.5"><i className="size-2.5 rounded-sm bg-ink" />was live</span>
          <span className="inline-flex items-center gap-1.5"><i className="size-2.5 rounded-sm bg-faint/50" />not live</span>
          <span className="inline-flex items-center gap-1.5"><i className="size-2.5 rounded-sm bg-accent" />dropped</span>
        </span>
      </figcaption>
      <p className="sr-only">On air {(t.uptime * 100).toFixed(1)}% of the time since {formatDate(new Date(t.from).toISOString())}, with {faults} drops.</p>
    </figure>
  );
}
